import {
  BillingEntity,
  ICreateLedger,
  ILedger,
} from "@shared/lib/types/briefCampains";
import styles from "./styles.module.scss";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Col, Flex, Modal, Row, Space } from "antd";
import { LedgerEntryForm } from "./LedgerEntryForm";
import { useCreateLedger } from "@entities/campaigns/lib/hooks/useCreateLedger";
import { useEditLedger } from "@entities/campaigns/lib/hooks/useEditLedger";
import { ToastService } from "@shared/lib/services";
import { format } from "date-fns";
import { useDeleteLedger } from "@entities/campaigns/lib/hooks";
import { Button } from "@shared/ui/Button";

export function LedgerDetails({
  ledgers,
  campaignInfluencerId,
  billing,
}: {
  ledgers: ILedger[];
  campaignInfluencerId: string;
  billing?: BillingEntity;
}) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState<ILedger | null>(null);

  const handleEditClick = (ledger: ILedger) => {
    setSelectedLedger(ledger);
    setIsEditModalOpen(true);
  };
  const { createLedger, isCreating, isCreated } =
    useCreateLedger(campaignInfluencerId);
  const { editLedger, isEditing, isEdited } =
    useEditLedger(campaignInfluencerId);
  const { deleteLedger, isDeleting, isDeleted, variables } =
    useDeleteLedger(campaignInfluencerId);

  useEffect(() => {
    if (isCreated || isEdited) {
      ToastService.success("Ledger created successfully");
      setIsEditModalOpen(false);
      setSelectedLedger(null);
    }
  }, [isCreated, isEdited]);

  useEffect(() => {
    if (isDeleted) {
      ToastService.success("Ledger deleted successfully");
    }
  }, [isDeleted]);

  const handleSubmit = (values: ICreateLedger) => {
    if (selectedLedger) {
      editLedger({
        ...values,
        ledgerId: selectedLedger._id,
        campaignInfluencerId,
      });
    } else {
      createLedger({ ...values, campaignInfluencerId });
    }
  };

  return (
    <>
      <div className={styles.ledgerCard}>
        <div className={styles.ledgerHeader}>
          <span className={styles.ledgerTitleMain}>Ledger</span>
          <button
            className={styles.ledgerAddBtn}
            onClick={() => {
              setIsEditModalOpen(true);
              setSelectedLedger(null);
            }}
          >
            <PlusOutlined style={{ marginRight: 4 }} /> Add Ledger
          </button>
        </div>
        <div className={styles.ledgerList}>
          {ledgers.map((entry) => {
            // Status badge logic
            let statusClass = styles.ledgerStatusError;
            let statusText = entry.subType;
            if (
              entry.type === "Debit" &&
              entry.subType === "Influencer-Purchase"
            ) {
              statusClass = styles.ledgerStatusSuccess;
              statusText = "Influencer Purchased";
            } else if (
              entry.type === "Debit" &&
              entry.subType === "Influencer-Billing-Reattached-Purchased"
            ) {
              statusClass = styles.ledgerStatusSuccess;
              statusText = "Influencer Billing Reattached Purchased";
            } else if (entry.subType === "Manual") {
              statusClass = styles.ledgerStatusManual;
              statusText = "Manual";
            }

            // Amount logic
            const isPositive = entry.type === "Debit";
            const amountClass = isPositive
              ? styles.ledgerAmount + " positive"
              : styles.ledgerAmount + " negative";
            const amountSign = isPositive ? "+" : "-";

            // Breakdown
            const breakdown = [
              entry.baseCost !== undefined
                ? `+ Base Cost: ₹${entry.baseCost}`
                : null,
              entry.gstAmount
                ? `+ GST (${entry.gstPercentage}%): ₹${entry.gstAmount}`
                : null,
              entry.tdsAmount
                ? `- TDS (${entry.tdsPercentage}%): ₹${entry.tdsAmount}`
                : null,
            ]
              .filter(Boolean)
              .join("\n");

            // Date and user (fallback to remarks or blank)
            const dateStr = entry.createdAt
              ? format(new Date(entry.createdAt), "dd MMM yyyy")
              : "-";
            const userStr = entry.createdBy
              ? `by ${entry.createdBy?.firstName} ${entry.createdBy?.lastName}`
              : "by Admin";

            return (
              <Row key={entry._id}>
                <Col span={8}>
                  <div className={styles.ledgerLeft} style={{ width: "100%" }}>
                    <div className={styles.ledgerTitle}>
                      {entry.remarks}

                      {entry.subType === "Manual" && (
                        <EditOutlined
                          className={styles.ledgerEditIcon}
                          onClick={() => handleEditClick(entry)}
                        />
                      )}
                    </div>
                    <div className={styles.ledgerDate}>
                      {dateStr} {userStr}
                    </div>
                  </div>
                </Col>
                <Col span={8} style={{ textAlign: "center" }}>
                  {statusText && (
                    <span className={`${styles.ledgerStatus} ${statusClass}`}>
                      {statusText}
                    </span>
                  )}
                </Col>
                <Col span={8}>
                  <Flex justify="end" gap={8}>
                    <div style={{ textAlign: "right" }}>
                      <div className={amountClass}>
                        {amountSign}₹
                        {(
                          entry.baseCost +
                          (entry.gstAmount || 0) -
                          (entry.tdsAmount || 0)
                        ).toFixed(2)}
                      </div>
                      {breakdown && (
                        <div className={styles.ledgerBreakdown}>
                          {breakdown.split("\n").map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    {entry.subType === "Manual" && (
                      <Button
                        danger
                        loading={
                          isDeleting && variables?.ledgerId === entry._id
                        }
                        onClick={() => deleteLedger({ ledgerId: entry._id })}
                        icon={<DeleteOutlined />}
                      />
                    )}
                  </Flex>
                </Col>
              </Row>
            );
          })}
        </div>
      </div>
      <Modal
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        destroyOnClose
        title={selectedLedger ? `Edit Ledger` : "Add Ledger"}
      >
        <LedgerEntryForm
          initialValues={
            selectedLedger
              ? {
                  type: selectedLedger.type,
                  baseCost: selectedLedger.baseCost,
                  gstPercentage: selectedLedger.gstPercentage,
                  tdsPercentage: selectedLedger.tdsPercentage,
                  hasGst: selectedLedger.isGst,
                  hasTds: selectedLedger.isTds,
                  remarks: selectedLedger.remarks,
                  campaignInfluencerId: selectedLedger.campaignInfluencerId,
                }
              : {
                  tdsPercentage: billing?.tdsPercentage || 10,
                }
          }
          onSubmit={handleSubmit}
          isSubmitting={isCreating || isEditing}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>
    </>
  );
}
